import { create } from 'zustand';
import axios from '../axios-base';

export interface ProjectGalleryItem {
    url: string;
    alt: string;
}

export interface ProjectBadge {
    _id: string;
    color: string;
    value: string;
    group: string;
}

export interface JuryProject {
    _id: string;
    contestantName: string;
    projectTitle?: string;
    projectCategory?: string;
    projectGallery?: ProjectGalleryItem[];
    voteCount?: number;
    voteCountFinale?: number;
    isFinalist?: boolean;
    badges?: ProjectBadge[];
}

export interface PollEntry {
    _id?: string;
    project: string | { _id: string };
    funding: number;
}

export interface JurorData {
    poll: PollEntry[];
    favoriteProjects: (string | { _id: string })[];
    voteIsConfirmed?: boolean;
}

export interface JuryProjectDetails {
    contestantAnswer1?: string;
    contestantAnswer2?: string;
    contestantAnswer3?: string;
    projectGallery?: ProjectGalleryItem[];
    [key: string]: any;
}

interface JuryState {
    projects: JuryProject[];
    categoryOptions: { value: string; displayValue: string }[];
    badges: ProjectBadge[];
    // poll: projectId → funding amount assigned (0 = unassigned)
    poll: Record<string, number>;
    bookmarks: Set<string>;
    loading: boolean;
    hydrated: boolean;
    isConfirmed: boolean;
    // Lazily-loaded full project details, keyed by project id.
    // `undefined` = never requested, `null` = in-flight, object = loaded.
    projectDetails: Record<string, JuryProjectDetails | null>;
}

interface JuryActions {
    fetchData: () => Promise<void>;
    fetchProjectDetails: (projectId: string) => Promise<void>;
    assignFunding: (projectId: string, amount: number | null) => Promise<void>;
    toggleBookmark: (projectId: string) => Promise<void>;
    confirmVotes: () => Promise<void>;
    reset: () => void;
}

type JuryStore = JuryState & JuryActions;

const resolveId = (ref: string | { _id: string }): string => (typeof ref === 'string' ? ref : ref._id);

const initialState: JuryState = {
    projects: [],
    categoryOptions: [],
    badges: [],
    poll: {},
    bookmarks: new Set(),
    loading: true,
    hydrated: false,
    isConfirmed: false,
    projectDetails: {},
};

/**
 * Zustand store for jury session state.
 * Data is fetched once per session (guarded by `hydrated`) and then kept
 * in memory, so navigating between Teilnehmer / Merkliste / Abstimmung
 * never triggers a refetch.
 *
 * Auth headers are injected automatically by the axios interceptor in
 * axios-base.js.
 */
const useJuryStore = create<JuryStore>((set, get) => ({
    ...initialState,

    fetchData: async () => {
        if (get().hydrated) return;

        try {
            const [projectsRes, optionsRes, jurorRes, badgesRes] = await Promise.all([
                axios.get('/jury/projects'),
                axios.get('/options/'),
                axios.get('/jury/me'),
                axios.get('/projectBadges'),
            ]);

            const categoryOptions: JuryState['categoryOptions'] =
                optionsRes.data[0]?.projectForm?.projectCategory?.elementConfig?.options ?? [];

            const poll: Record<string, number> = {};
            const bookmarks = new Set<string>();
            let isConfirmed = false;

            const juror: JurorData = jurorRes.data;
            juror.poll.forEach((item) => {
                poll[resolveId(item.project)] = item.funding;
            });
            juror.favoriteProjects.forEach((p) => {
                bookmarks.add(resolveId(p));
            });
            isConfirmed = juror.voteIsConfirmed ?? false;

            // Map badge IDs in projects to full badge objects
            const badges: ProjectBadge[] = badgesRes.data;
            const badgeMap = new Map(badges.map((b) => [b._id, b]));
            const projects: JuryProject[] = projectsRes.data.map((p: any) => ({
                ...p,
                badges: (p.badges || []).map((id: string) => badgeMap.get(id)).filter(Boolean),
            }));

            set({
                projects,
                categoryOptions,
                badges,
                poll,
                bookmarks,
                loading: false,
                hydrated: true,
                isConfirmed,
            });
        } catch (err) {
            console.error('Error fetching jury data:', err);
            set({ loading: false, hydrated: true });
        }
    },

    fetchProjectDetails: async (projectId) => {
        const cached = get().projectDetails[projectId];
        // Already loaded or already in-flight
        if (cached !== undefined) return;

        // Mark as in-flight so concurrent callers don't fire again
        set((state) => ({ projectDetails: { ...state.projectDetails, [projectId]: null } }));

        try {
            const res = await axios.get(`/projects/${projectId}`);
            set((state) => ({
                projectDetails: { ...state.projectDetails, [projectId]: res.data },
            }));
        } catch (err) {
            console.error('Failed to load project details:', err);
            // Drop the in-flight marker so a future call can retry
            set((state) => {
                const { [projectId]: _, ...rest } = state.projectDetails;
                return { projectDetails: rest };
            });
        }
    },

    assignFunding: async (projectId, amount) => {
        if (get().isConfirmed) return;
        const { poll } = get();
        const currentAmount = poll[projectId] ?? null;

        // Optimistic update
        const nextPoll = { ...poll };
        if (amount === null) {
            delete nextPoll[projectId];
        } else {
            nextPoll[projectId] = amount;
        }
        set({ poll: nextPoll });

        try {
            if (currentAmount !== null) {
                // Remove existing allocation first
                await axios.delete('/jury/me/vote', { data: { projectId } });
            }
            if (amount !== null) {
                // Add new allocation
                await axios.patch('/jury/me/vote', { projectId, funding: amount });
            }
        } catch (err) {
            console.error('Funding assignment failed:', err);
            // Roll back
            const rolledBack = { ...get().poll };
            if (currentAmount === null) {
                delete rolledBack[projectId];
            } else {
                rolledBack[projectId] = currentAmount;
            }
            set({ poll: rolledBack });
        }
    },

    toggleBookmark: async (projectId) => {
        if (get().isConfirmed) return;
        const { bookmarks } = get();
        const wasBookmarked = bookmarks.has(projectId);

        const next = new Set(bookmarks);
        if (wasBookmarked) next.delete(projectId);
        else next.add(projectId);
        set({ bookmarks: next });

        try {
            if (wasBookmarked) {
                await axios.delete('/jury/me/favorite', { data: { favoriteProject: projectId } });
            } else {
                await axios.post('/jury/me/favorite', { favoriteProject: projectId });
            }
        } catch (err) {
            console.error('Bookmark sync failed:', err);
            const rolled = new Set(get().bookmarks);
            if (wasBookmarked) rolled.add(projectId);
            else rolled.delete(projectId);
            set({ bookmarks: rolled });
        }
    },

    confirmVotes: async () => {
        try {
            await axios.patch('/jury/me/confirm', {});
            set({ isConfirmed: true });
        } catch (err) {
            console.error('Vote confirmation failed:', err);
            throw err;
        }
    },

    // Call this on logout
    reset: () => set({ ...initialState, bookmarks: new Set() }),
}));

export default useJuryStore;
